import { useCallback, useEffect, useState } from 'react';
import { unionRoleLabel } from '../../../shared/domain/unionAccess.js';
import {
  loadUnionMemberDirectory,
  removeUnionMember,
  setUnionMemberRole,
  transferUnionLeadership,
} from '../api/unionMemberManagementApi.js';

const EMPTY_DIRECTORY = {
  unionId: '',
  unionName: '',
  viewerRole: '',
  members: [],
};

export function useUnionMemberManagement() {
  const [directory, setDirectory] = useState(EMPTY_DIRECTORY);
  const [loading, setLoading] = useState(true);
  const [actionMembershipId, setActionMembershipId] = useState('');
  const [actionMode, setActionMode] = useState('');
  const [notice, setNotice] = useState({ type: '', message: '' });

  const refresh = useCallback(async ({ preserveSuccess = false } = {}) => {
    setLoading(true);

    try {
      const result = await loadUnionMemberDirectory();
      setDirectory(result);
      if (!preserveSuccess) setNotice({ type: '', message: '' });
    } catch (error) {
      setDirectory(EMPTY_DIRECTORY);
      setNotice({
        type: 'error',
        message: error instanceof Error ? error.message : '유니온 멤버 목록을 불러오지 못했습니다.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;

    loadUnionMemberDirectory()
      .then((result) => {
        if (active) setDirectory(result);
      })
      .catch((error) => {
        if (!active) return;
        setNotice({
          type: 'error',
          message: error instanceof Error ? error.message : '유니온 멤버 목록을 불러오지 못했습니다.',
        });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    const handleMembershipChanged = () => {
      refresh();
    };

    window.addEventListener('dororaid:union-membership-changed', handleMembershipChanged);

    return () => {
      active = false;
      window.removeEventListener('dororaid:union-membership-changed', handleMembershipChanged);
    };
  }, [refresh]);

  const changeRole = useCallback(async ({ membershipId, nickname, role }) => {
    if (!membershipId || actionMembershipId) return;

    setActionMembershipId(membershipId);
    setActionMode('role');
    setNotice({ type: '', message: '' });

    try {
      await setUnionMemberRole({ membershipId, role });
      const result = await loadUnionMemberDirectory();
      setDirectory(result);
      setNotice({
        type: 'success',
        message: `${nickname || '멤버'} 역할을 ${unionRoleLabel(role)}으로 변경했습니다.`,
      });
      window.dispatchEvent(new CustomEvent('dororaid:union-membership-changed'));
    } catch (error) {
      setNotice({
        type: 'error',
        message: error instanceof Error ? error.message : '멤버 역할 변경에 실패했습니다.',
      });
    } finally {
      setActionMembershipId('');
      setActionMode('');
    }
  }, [actionMembershipId]);

  const transferLeadership = useCallback(async ({ membershipId, nickname }) => {
    if (!membershipId || actionMembershipId) return;

    setActionMembershipId(membershipId);
    setActionMode('leadership');
    setNotice({ type: '', message: '' });

    try {
      await transferUnionLeadership(membershipId);
      const result = await loadUnionMemberDirectory();
      setDirectory(result);
      setNotice({
        type: 'success',
        message: `${nickname || '선택한 멤버'}에게 주딱 권한과 유니온 소유 권한을 이전했습니다. 현재 계정은 파딱으로 전환됐습니다.`,
      });
      window.dispatchEvent(new CustomEvent('dororaid:union-membership-changed'));
    } catch (error) {
      setNotice({
        type: 'error',
        message: error instanceof Error ? error.message : '주딱 권한 이전에 실패했습니다.',
      });
    } finally {
      setActionMembershipId('');
      setActionMode('');
    }
  }, [actionMembershipId]);

  const remove = useCallback(async ({ membershipId, nickname }) => {
    if (!membershipId || actionMembershipId) return;

    setActionMembershipId(membershipId);
    setActionMode('remove');
    setNotice({ type: '', message: '' });

    try {
      await removeUnionMember(membershipId);
      const result = await loadUnionMemberDirectory();
      setDirectory(result);
      setNotice({
        type: 'success',
        message: `${nickname || '멤버'} 계정을 유니온에서 제외했습니다.`,
      });
      window.dispatchEvent(new CustomEvent('dororaid:union-membership-changed'));
    } catch (error) {
      setNotice({
        type: 'error',
        message: error instanceof Error ? error.message : '멤버 제외에 실패했습니다.',
      });
    } finally {
      setActionMembershipId('');
      setActionMode('');
    }
  }, [actionMembershipId]);

  return {
    ...directory,
    loading,
    actionMembershipId,
    actionMode,
    notice,
    refresh,
    changeRole,
    transferLeadership,
    remove,
  };
}
